import estimateKeys from '@/api/queries/estimates/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import {
  EstimateBuilderEstimateFragment,
  GetEstimatePreviewQuery,
  GetProjectEstimatesQuery,
} from '@/gql/graphql';
import { QueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

type HydratedEstimate = EstimateBuilderEstimateFragment & {
  preview: NonNullable<GetEstimatePreviewQuery['estimate']>['preview'];
};

/**
 * Shared mutation-scope id for every optimistic mutation against a given
 * estimate. Used by `createOptimisticEstimateMutation` to serialise
 * same-estimate mutations, and here to detect when other mutations on the
 * same estimate are still pending.
 */
export const estimateMutationScopeId = (estimateId: string) =>
  `estimateMutations:${estimateId}`;

/**
 * Apply a mutation's `Estimate!` response to the relevant TanStack caches
 * without triggering a follow-up GET:
 *
 *  - patches the single estimate in the `GetProjectEstimates` cache so
 *    other estimates on the project aren't disturbed
 *  - writes the rendered Liquid preview into the `estimatePreview` cache
 *    so `useGetEstimatePreview` re-renders from the same roundtrip
 *
 * When other estimate-scoped mutations are still pending, the projects
 * cache write is skipped: those mutations' optimistic updates are sitting
 * in the cache, and writing this older server snapshot on top would clobber
 * them. The next mutation's onSuccess will hydrate from a server response
 * that includes the queued changes. The preview cache is always written
 * because it isn't a target for optimistic updates from other mutations.
 */
export function hydrateEstimateCaches(
  queryClient: QueryClient,
  args: { projectId: string; estimateId: string; estimate: HydratedEstimate }
) {
  const { projectId, estimateId, estimate } = args;

  // Within a serialised scope, exactly one mutation has `isPaused: false`
  // (the one actively running its callbacks — i.e. us). Queued same-scope
  // mutations dispatch `pending` synchronously with `isPaused: true` while
  // they wait in the retryer queue, so filtering on `isPaused` reliably
  // excludes self without depending on TanStack's self-counting quirk in
  // `isMutating` (see github.com/TanStack/query/issues/5545).
  const otherPending = queryClient.isMutating({
    predicate: (m) =>
      m.options.scope?.id === estimateMutationScopeId(estimateId) &&
      m.state.status === 'pending' &&
      m.state.isPaused,
  });

  if (otherPending === 0) {
    queryClient.setQueryData<GetProjectEstimatesQuery>(
      projectKeys.estimates(projectId),
      (old) =>
        old &&
        produce(old, (draft) => {
          const idx = draft.projectById.estimates.findIndex(
            (e) => e.id === estimateId
          );
          if (idx === -1) return;
          // `preview` is on the mutation response but not on the project query's
          // type — strip it so the cache shape stays aligned.
          const { preview: _preview, ...rest } = estimate;
          draft.projectById.estimates[idx] = rest;
        })
    );
  }

  queryClient.setQueryData<GetEstimatePreviewQuery>(
    estimateKeys.estimatePreview(estimateId),
    { estimate: { id: estimate.id, preview: estimate.preview } }
  );
}
